import { useEffect, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { authClient } from "@/auth/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo, LogoMark } from "@/components/Logo";
import { OtpCodeInput } from "@/components/auth/OtpCodeInput";
import { cn } from "@/lib/utils";
import { authErrorMessage, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from "@/lib/auth-error";
import {
  Envelope,
  ArrowRight,
  CircleNotch,
  Key,
  CaretDown,
  CheckCircle,
} from "@phosphor-icons/react";

export const Route = createFileRoute("/register")({
  component: RegisterPage,
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: (
      typeof search.redirect === "string" ? search.redirect : ""
    ) as string,
  }),
});

function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { redirect: redirectTo } = Route.useSearch();

  const [otpEmail, setOtpEmail] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const autoSubmittedOtpRef = useRef("");

  async function handleSendOtp(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await authClient.emailOtp.sendVerificationOtp({
        email: otpEmail.trim(),
        type: "sign-in",
      });
      if (result.error) {
        setError(authErrorMessage(result.error, t, "register.otpError"));
      } else {
        setOtpSent(true);
      }
    } catch {
      setError(t("register.genericError"));
    } finally {
      setLoading(false);
    }
  }

  async function verifyOtp() {
    setError("");
    if (otp.length < 6) return;
    setLoading(true);
    try {
      const result = await authClient.signIn.emailOtp({
        email: otpEmail.trim(),
        otp: otp.trim(),
      });
      if (result.error) {
        setError(authErrorMessage(result.error, t, "register.otpInvalid"));
        setOtp("");
      } else {
        await queryClient.invalidateQueries({ queryKey: ["auth", "session"] });
        navigate({ to: "/post-login", search: { redirect: redirectTo || "/" } });
      }
    } catch {
      setError(t("register.genericError"));
      setOtp("");
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyOtp(e: React.FormEvent) {
    e.preventDefault();
    await verifyOtp();
  }

  useEffect(() => {
    if (otp.length < 6) {
      autoSubmittedOtpRef.current = "";
      return;
    }
    if (!otpSent || loading || autoSubmittedOtpRef.current === otp) return;
    autoSubmittedOtpRef.current = otp;
    void verifyOtp();
  }, [otp, otpSent, loading]);

  function validate(): boolean {
    const errors: Record<string, string> = {};
    if (!name.trim()) errors.name = t("register.nameRequired");
    if (!email.trim()) errors.email = t("register.emailRequired");
    if (password.length < PASSWORD_MIN_LENGTH)
      errors.password = t("register.passwordMinLength");
    else if (password.length > PASSWORD_MAX_LENGTH)
      errors.password = t("auth.errors.passwordTooLong");
    if (password !== confirmPassword)
      errors.confirmPassword = t("register.passwordMismatch");
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleRegister(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (!validate()) return;
    setLoading(true);
    try {
      const result = await authClient.signUp.email({
        name: name.trim(),
        email: email.trim(),
        password,
      });
      if (result.error) {
        setError(authErrorMessage(result.error, t, "register.registerError"));
      } else {
        await queryClient.invalidateQueries({ queryKey: ["auth", "session"] });
        navigate({ to: "/post-login", search: { redirect: redirectTo || "/" } });
      }
    } catch {
      setError(t("register.genericError"));
    } finally {
      setLoading(false);
    }
  }

  async function handleSocialLogin(provider: "google") {
    setError("");
    try {
      await authClient.signIn.social({
        provider,
        callbackURL: `/post-login?redirect=${encodeURIComponent(redirectTo || "/")}`,
      });
    } catch {
      setError(t("register.socialError"));
    }
  }

  return (
    <div className="flex min-h-screen bg-background">
      {/* Left brand panel -- desktop only */}
      <div
        className="relative hidden w-[46%] flex-col justify-between overflow-hidden p-12 text-white lg:flex xl:p-16"
        style={{
          background:
            "linear-gradient(155deg, var(--color-brand-dark), var(--color-brand) 58%, var(--color-brand-dark))",
        }}
      >
        {/* Soft gold halo */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(55% 45% at 82% 8%, oklch(74.72% 0.089 82.39 / 0.22), transparent 70%)",
          }}
        />
        {/* Oversized logo-mark motif, bleeding off-edge */}
        <LogoMark
          aria-hidden
          className="pointer-events-none absolute -bottom-24 -left-24 size-[28rem] opacity-[0.06]"
          style={{ ["--color-brand" as string]: "white" }}
        />
        {/* Fine grain texture */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.5] mix-blend-soft-light"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.35'/%3E%3C/svg%3E\")",
          }}
        />

        <Link
          to="/"
          className="relative flex items-center transition-opacity hover:opacity-80"
        >
          {/* Gold dots + white wordmark on the dark panel */}
          <Logo
            className="h-7 w-auto text-white"
            title="Psihotron"
            style={{ ["--color-brand" as string]: "var(--color-accent-gold)" }}
          />
        </Link>

        <div className="relative max-w-md">
          <h2
            className="text-balance text-4xl font-semibold leading-[1.1] xl:text-[2.9rem]"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            {t("register.brandTitle")}{" "}
            <span style={{ color: "var(--color-accent-gold)" }}>
              {t("register.brandHighlight")}
            </span>
          </h2>
          <p className="mt-6 text-[0.95rem] leading-relaxed text-white/70">
            {t("register.brandSubtitle")}
          </p>
        </div>

        <div className="relative flex items-center gap-4">
          {/* 4-dot motif echo */}
          <span aria-hidden className="grid grid-cols-2 gap-1">
            {Array.from({ length: 4 }).map((_, i) => (
              <span
                key={i}
                className="size-1.5 rounded-full"
                style={{ background: "var(--color-accent-gold)", opacity: 0.7 }}
              />
            ))}
          </span>
          <p className="text-xs text-white/45">
            {t("footer.copyright", { year: new Date().getFullYear() })}
          </p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex flex-1 flex-col items-center justify-center px-5 py-12 sm:px-8">
        <div className="animate-ink-rise w-full max-w-sm">
          {/* Mobile logo */}
          <Link to="/" className="mb-10 inline-flex lg:hidden">
            <Logo className="h-7 w-auto text-foreground" title="Psihotron" />
          </Link>

          <h1
            className="text-3xl font-semibold tracking-tight text-foreground sm:text-[2rem]"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            {t("register.title")}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("register.subtitle")}
          </p>

          {/* Error */}
          {error && (
            <div className="mt-6 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          )}

          {/* Email OTP -- primary */}
          <div className="mt-8">
            {otpSent ? (
              <form
                onSubmit={handleVerifyOtp}
                className="rounded-2xl border p-6 text-center"
                style={{
                  borderColor: "var(--color-brand-bg-light)",
                  background: "var(--color-brand-bg-light)",
                }}
              >
                <div
                  className="mx-auto flex size-12 items-center justify-center rounded-full"
                  style={{ background: "var(--color-brand-bg)" }}
                >
                  <CheckCircle
                    className="size-6"
                    style={{ color: "var(--color-brand)" }}
                  />
                </div>
                <p className="mt-3 text-sm font-medium text-foreground">
                  {t("register.otpSent")}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {t("register.checkInbox")}{" "}
                  <span className="font-medium text-foreground">
                    {otpEmail}
                  </span>
                </p>
                <Label htmlFor="register-otp" className="mt-4 block text-left text-xs">
                  {t("register.otpLabel")}
                </Label>
                <OtpCodeInput
                  id="register-otp"
                  value={otp}
                  onChange={(value) => {
                    setOtp(value);
                    setError("");
                  }}
                  invalid={!!error}
                  autoFocus
                  className="mt-2"
                />
                <Button
                  type="submit"
                  size="lg"
                  disabled={loading || otp.length < 6}
                  className="mt-3 h-11 w-full rounded-xl"
                >
                  {loading ? (
                    <CircleNotch className="animate-spin" data-icon="inline-start" />
                  ) : null}
                  {loading ? t("register.verifying") : t("register.verifyButton")}
                </Button>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  onClick={() => {
                    setOtpSent(false);
                    setOtpEmail("");
                    setOtp("");
                    setError("");
                  }}
                  className="mt-3 h-auto p-0 text-xs"
                >
                  {t("register.otpUseDifferent")}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleSendOtp} className="flex flex-col gap-3">
                <Label htmlFor="otp-email-reg">{t("register.emailLabel")}</Label>
                <div className="relative">
                  <Envelope className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="otp-email-reg"
                    type="email"
                    placeholder={t("register.emailPlaceholder")}
                    value={otpEmail}
                    onChange={(e) => setOtpEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="h-11 rounded-xl pl-10"
                  />
                </div>
                <Button
                  type="submit"
                  size="lg"
                  disabled={loading || !otpEmail.trim()}
                  className="h-11 w-full rounded-xl"
                >
                  {loading ? (
                    <CircleNotch className="animate-spin" data-icon="inline-start" />
                  ) : null}
                  {t("register.continueEmail")}
                  {!loading && <ArrowRight data-icon="inline-end" />}
                </Button>
                <p className="text-xs text-muted-foreground">
                  {t("register.otpHint")}
                </p>
              </form>
            )}
          </div>

          {/* Divider */}
          <div className="relative my-7">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-background px-3 text-xs uppercase tracking-wider text-muted-foreground">
                {t("register.or")}
              </span>
            </div>
          </div>

          {/* Social login */}
          <Button
            variant="outline"
            size="lg"
            onClick={() => handleSocialLogin("google")}
            disabled={loading}
            className="h-11 w-full rounded-xl"
          >
            <GoogleIcon className="size-4" />
            {t("register.continueGoogle")}
          </Button>

          {/* Password toggle */}
          <div className="mt-5">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowPassword(!showPassword);
                setError("");
              }}
              className="w-full text-xs text-muted-foreground hover:text-foreground"
            >
              <Key className="size-3.5" />
              {t("register.passwordRegister")}
              <CaretDown
                className={cn(
                  "size-3.5 transition-transform",
                  showPassword && "rotate-180"
                )}
              />
            </Button>

            <div
              className={cn(
                "grid transition-all duration-300 ease-in-out",
                showPassword ? "mt-3 grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
              )}
            >
              <div className="overflow-hidden">
                <form
                  onSubmit={handleRegister}
                  className="flex flex-col gap-3 rounded-xl border bg-muted/30 p-4"
                >
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="reg-name" className="text-xs">
                      {t("register.nameLabel")}
                    </Label>
                    <Input
                      id="reg-name"
                      type="text"
                      placeholder={t("register.namePlaceholder")}
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        setFieldErrors((p) => ({ ...p, name: "" }));
                      }}
                      required
                      maxLength={200}
                      autoComplete="name"
                      aria-invalid={!!fieldErrors.name}
                      className="rounded-lg"
                    />
                    {fieldErrors.name && (
                      <p className="text-[11px] text-destructive">
                        {fieldErrors.name}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="reg-email" className="text-xs">
                      {t("register.emailLabel")}
                    </Label>
                    <Input
                      id="reg-email"
                      type="email"
                      placeholder={t("register.emailPlaceholder")}
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setFieldErrors((p) => ({ ...p, email: "" }));
                      }}
                      required
                      autoComplete="email"
                      aria-invalid={!!fieldErrors.email}
                      className="rounded-lg"
                    />
                    {fieldErrors.email && (
                      <p className="text-[11px] text-destructive">
                        {fieldErrors.email}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="reg-password" className="text-xs">
                      {t("register.passwordLabel")}
                    </Label>
                    <Input
                      id="reg-password"
                      type="password"
                      placeholder={t("register.passwordPlaceholder")}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setFieldErrors((p) => ({ ...p, password: "" }));
                      }}
                      required
                      maxLength={PASSWORD_MAX_LENGTH}
                      autoComplete="new-password"
                      aria-invalid={!!fieldErrors.password}
                      className="rounded-lg"
                    />
                    {fieldErrors.password && (
                      <p className="text-[11px] text-destructive">
                        {fieldErrors.password}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="reg-confirm" className="text-xs">
                      {t("register.confirmPasswordLabel")}
                    </Label>
                    <Input
                      id="reg-confirm"
                      type="password"
                      placeholder={t("register.confirmPasswordPlaceholder")}
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        setFieldErrors((p) => ({ ...p, confirmPassword: "" }));
                      }}
                      required
                      maxLength={PASSWORD_MAX_LENGTH}
                      autoComplete="new-password"
                      aria-invalid={!!fieldErrors.confirmPassword}
                      className="rounded-lg"
                    />
                    {fieldErrors.confirmPassword && (
                      <p className="text-[11px] text-destructive">
                        {fieldErrors.confirmPassword}
                      </p>
                    )}
                  </div>
                  <Button type="submit" className="w-full rounded-lg" disabled={loading}>
                    {loading ? (
                      <CircleNotch className="animate-spin" data-icon="inline-start" />
                    ) : null}
                    {loading ? t("register.registering") : t("register.registerButton")}
                  </Button>
                </form>
              </div>
            </div>
          </div>

          {/* Footer link */}
          <p className="mt-8 text-center text-sm text-muted-foreground">
            {t("register.hasAccount")}{" "}
            <Link
              to="/login"
              search={{ redirect: redirectTo }}
              className="font-medium text-primary hover:underline"
            >
              {t("register.loginLink")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

/* --- Social Icons --- */

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}
